import { useContext, useEffect, useState, useRef } from "react";
import SelectInput from "../ui/SelectInput";
import DatePickerInput from "../ui/DatePickerInput";
import { currencyList } from "../../data/currencyList";
import FromToInput from "../ui/FromToInput";
import TimePickerInput from "../ui/TimePickerInput";
import TextAreaInput from "../ui/TextAreaInput";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import useModal from "../../hooks/useModal";
import usePost from "../../hooks/usePost";
import PortalQuoteQueryPersonalDetails from "./PortalQuoteQueryPersonalDetails";
import getClassName from "../../utilities/getClassName";
import { setMinDate, setMaxDate, setMinTime, formatTimeTo24h, dateStringToApiFormat } from "../../utilities/date";
import poweredByContext from "../../store/PoweredByContext";
import useGeoLocation from "react-ipgeolocation";
import PortalAvinodeCtaModal from "./PortalAvinodeCtaModal";
import TripTypeTabs from "../shared/TripTypeTabs";

// Icons
import chevronDown from "../../assets/icons/chevron-down-big.svg";
import personIcon from "../../assets/icons/person-dark.svg";
import luggageIcon from "../../assets/icons/luggage-dark.svg";
import currencyIcon from "../../assets/icons/quote-payment.svg";
import plusDarkIcon from "../../assets/icons/plus-dark.svg";
import closeCrossDark from "../../assets/icons/close-cross-dark-heavy.svg";
import exclamationIcon from "../../assets/icons/exclamation-mark.svg";
import { getIcon, getRawIcon } from "../../utilities/icon";
import LeadClientInput from "../shared/LeadClientInput";
import PortalTenantIdInput from "./PortalTenantIdInput";
import TextInput from "../ui/TextInput";
import portalContext from "../../store/PortalContext";
import LoadingOverlay from "../shared/LoadingOverlay";
import {
    canDeleteJourneyLeg,
    syncReturnLegs,
    getInitialJourneyDataForTripType,
    transformJourneysForTripTypeChange,
} from "../../utilities/quoteQueryJets";
import PassengerDropdown from "../manage-passengers/PassengerDropdown";
import LuggageDropdown from "../shared/LuggageDropdown";

const PortalQuoteQueryJets = ({
    onFocus,
    inModal,
    onPage,
    closeQuoteModal,
    onModalSuccess,
    updateSending,
    quoteOrBookingToCopy,
    skeleton,
    tripType: propTripType,
    setTripType: propSetTripType,
}) => {
    const [internalTripType, setInternalTripType] = useState("oneway");
    const tripType = propTripType || internalTripType;
    const setTripType = propSetTripType || setInternalTripType;
    const [journeyCounter, setJourneyCounter] = useState(() => (tripType === "oneway" ? 1 : 2));
    const [journeyData, setJourneyData] = useState(() => getInitialJourneyDataForTripType(tripType));
    // Tracks the trip type the current journeyData was built for, so we can
    // transform (rather than wipe) the legs when the trip type changes.
    const prevTripTypeRef = useRef(tripType);
    const [validateForm, setValidateForm] = useState(false);
    const [formIsValid, setFormIsValid] = useState(false);
    const [showError, setShowError] = useState(false);
    const [resetFormRef, setResetFormRef] = useState(0);
    const isScrolled = false;
    const [mobileIsActive, setMobileIsActive] = useState(false);
    const quoteQueryMarkerHeight = 0;
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [avinodeUrl, setAvinodeUrl] = useState();

    const [zohoUser, setZohoUser] = useState({ id: "", type: "" });

    const [ctaUsed, setCtaUsed] = useState("");

    // const [createdJourneyId, setCreatedJourneyId] = useState(null);

    const [selectedPrefCurrency, setSelectedPrefCurrency] = useState("gbp");
    const [selectedTenantId, setSelectedTenantId] = useState("");
    const [currentNotes, setCurrentNotes] = useState("");

    // Hooks
    const { modalVisible, closeModal } = useModal();
    const { sending, returnMessage, returnData: submitReqReturnData, postData } = usePost();
    const location = useGeoLocation();

    const poweredByCtx = useContext(poweredByContext);
    const portalCtx = useContext(portalContext);

    const quoteQueryRef = useRef();
    const quoteQueryMarkerRef = useRef();
    const quoteQueryMarkerRef2 = useRef();

    const checkOverflow = () => {
        const root = document.documentElement;
        let valid = false;

        if (isScrolled && mobileIsActive && !window.matchMedia("(min-width: 765px)").matches) {
            root.style.overflow = "hidden";
            valid = true;
        } else {
            root.style.overflow = null;
        }

        return valid;
    };

    // The tabs only set the trip type; the effect further down reacts to the
    // change (this also covers the trip type being controlled by a parent page).
    const handleTripTypeChange = (type) => {
        setTripType(type);
    };

    const addJourneyHandler = () => {
        setJourneyCounter(journeyCounter + 1);

        const lastJourney = journeyData[journeyData.length - 1];

        let newDate = "";

        const newJourney = {
            journeyType: "private-jets",
            id: journeyCounter + 1,
            ...(tripType === "returns"
                ? {
                      flightFrom: lastJourney["flightTo"],
                      flightTo: lastJourney["flightFrom"],
                  }
                : {}),
            date: newDate,
            time: lastJourney["time"],
            luggage: lastJourney["luggage"],
            carryOn: lastJourney["carryOn"],
            passengers: lastJourney["passengers"],
        };

        const addJourney = [...journeyData, newJourney];

        setJourneyData(addJourney);
        setValidateForm(false);
        setFormIsValid(false);
    };

    const removeJourneyHandler = (index) => {
        let removeJourney = [];
        for (let i = 0; i < journeyData.length; i++) {
            if (i !== index) {
                removeJourney = [...removeJourney, journeyData[i]];
            }
        }
        setJourneyData(removeJourney);
    };

    const resetJourneyHandler = () => {
        setValidateForm(false);
        setFormIsValid(false);
        setJourneyCounter(1);
        window.scrollTo(0, 0);
        setResetFormRef(resetFormRef + 1);
        setJourneyData([{ id: 1, passengers: 1, luggage: 1, carryOn: 1 }]);
    };

    const getTime = (time) => (location?.country === "US" ? formatTimeTo24h(time) : time);

    // Submit
    const submitToAvinodeHandler = (e) => {
        e.preventDefault();
        setCtaUsed("avinode");
        e.preventDefault();
        setValidateForm(true);

        let formattedJourneyData = [];
        journeyData.map((flight) => {
            const addFlights = {
                flightFrom: flight.journeyType === "helicopters" ? flight["flightFrom"] : flight["flightFrom"]["icao"],
                flightTo: flight.journeyType === "helicopters" ? flight["flightTo"] : flight["flightTo"]["icao"],
                date: dateStringToApiFormat(flight["date"]),
                time: getTime(flight["time"]),
                numberOfPassengers: flight["passengers"],
                itemsOfLuggage: flight["luggage"] ?? 0,
                itemsOfCarryOn: flight["carryOn"] ?? 0,
                journeyType: flight["journeyType"],
            };
            formattedJourneyData = [...formattedJourneyData, addFlights];
            return false;
        });

        let formData = {};
        formData = {
            journeys: formattedJourneyData,
            ZohoUser: zohoUser,
            additionalRequests: currentNotes,
            additionalAction: "avinode",
            tenantID: selectedTenantId,
            preferredCurrency: selectedPrefCurrency,
            createdBy: portalCtx.id,
        };

        postData(formData, "/api/flight/search-flights-by-member", 1000);
    };

    const submitToRFQHandler = (e) => {
        setCtaUsed("rfq");
        e.preventDefault();
        setValidateForm(true);

        let formattedJourneyData = [];
        journeyData.map((flight) => {
            const addFlights = {
                flightFrom: flight.journeyType === "helicopters" ? flight["flightFrom"] : flight["flightFrom"]["icao"],
                flightTo: flight.journeyType === "helicopters" ? flight["flightTo"] : flight["flightTo"]["icao"],
                date: dateStringToApiFormat(flight["date"]),
                time: getTime(flight["time"]),
                numberOfPassengers: flight["passengers"],
                itemsOfLuggage: flight["luggage"] ?? 0,
                itemsOfCarryOn: flight["carryOn"] ?? 0,
                journeyType: flight["journeyType"],
            };
            formattedJourneyData = [...formattedJourneyData, addFlights];
            return false;
        });

        let formData = {};
        formData = {
            journeys: formattedJourneyData,
            ZohoUser: zohoUser,
            additionalRequests: currentNotes,
            additionalAction: "rfq",
            tenantID: selectedTenantId,
            preferredCurrency: selectedPrefCurrency,
            createdBy: portalCtx.id,
        };

        postData(formData, "/api/flight/search-flights-by-member", 1000);
    };

    useEffect(() => {
        if (returnMessage && submitReqReturnData && !returnMessage.success) {
            console.log(returnMessage);
        } else if (submitReqReturnData) {
            //console.log(submitReqReturnData)
            console.log(ctaUsed);
            if (ctaUsed === "avinode" && submitReqReturnData.avinodeUrl) {
                //open avinode url
                console.log(submitReqReturnData.avinodeUrl);
                setAvinodeUrl(submitReqReturnData.avinodeUrl);
            } else if (ctaUsed === "rfq") {
                //navigate('/portal/trips')
                window.location.reload();
            } else {
                window.location.reload();
            }
        }
    }, [submitReqReturnData, returnMessage, ctaUsed]);

    const closeAvinodeModal = () => {
        window.location.reload();
    };

    const submitHandler = (e) => {
        setCtaUsed("default");
        e.preventDefault();
        setValidateForm(true);

        let formattedJourneyData = [];
        journeyData.map((flight) => {
            const addFlights = {
                flightFrom: flight.journeyType === "helicopters" ? flight["flightFrom"] : flight["flightFrom"]["icao"],
                flightTo: flight.journeyType === "helicopters" ? flight["flightTo"] : flight["flightTo"]["icao"],
                date: dateStringToApiFormat(flight["date"]),
                time: getTime(flight["time"]),
                numberOfPassengers: flight["passengers"],
                itemsOfLuggage: flight["luggage"] ?? 0,
                itemsOfCarryOn: flight["carryOn"] ?? 0,
                journeyType: flight["journeyType"],
            };
            formattedJourneyData = [...formattedJourneyData, addFlights];
            return false;
        });

        let formData = {};
        formData = {
            journeys: formattedJourneyData,
            ZohoUser: zohoUser,
            additionalRequests: currentNotes,
            tenantID: selectedTenantId,
            preferredCurrency: selectedPrefCurrency,
            createdBy: portalCtx.id,
        };

        postData(formData, "/api/flight/search-flights-by-member", 1000);
    };

    // Validate Form
    const validateFormHandler = () => {
        let errors = false;

        journeyData.map((journey) => {
            let dateIsInPast = false;

            if (journey.date) {
                const dateParts = journey.date.split("/");
                const date = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
                const today = new Date();

                today.setHours(0, 0, -1);

                dateIsInPast = date < today;
            }

            if (!journey.flightFrom) {
                errors++;
            }

            if (!journey.flightTo) {
                errors++;
            }

            if (!journey.date || dateIsInPast) {
                errors++;
            }

            if (!journey.time) {
                errors++;
            }

            return false;
        });

        if (!errors) {
            setShowError(false);
            setFormIsValid(true);
        } else {
            setFormIsValid(false);
        }
    };

    useEffect(() => {
        validateFormHandler();
    });

    // When the trip type changes, transform the existing legs instead of
    // clearing them. Bumping resetFormRef remounts the inputs so they re-seed
    // (and re-select) from the preserved journey data.
    useEffect(() => {
        const prevType = prevTripTypeRef.current;
        if (prevType === tripType) return;
        prevTripTypeRef.current = tripType;

        const { journeys, counter } = transformJourneysForTripTypeChange(
            prevType,
            tripType,
            journeyData,
            journeyCounter
        );
        setJourneyData(journeys);
        setJourneyCounter(counter);
        setValidateForm(false);
        setFormIsValid(false);
        setShowError(false);
        setResetFormRef((r) => r + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tripType, journeyData, journeyCounter]);

    // onModalSuccess / updateSending are caller-supplied callbacks; many
    // callers pass inline arrows. Including them here re-fires the effect
    // every render (same trap as LuggageDropdown). The effect only acts on
    // returnMessage / sending, which is the real trigger we care about.
    useEffect(() => {
        if (returnMessage.success) {
            onModalSuccess();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [returnMessage]);

    useEffect(() => {
        if (typeof updateSending === "function") {
            updateSending(sending);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sending]);

    useEffect(() => {
        if (quoteOrBookingToCopy) {
            quoteOrBookingToCopy.legs.forEach((leg, i) => {
                if (i > 0) {
                    addJourneyHandler();
                }
            });
        }

        const resizeHandler = () => {
            checkOverflow();
        };

        resizeHandler();
        window.addEventListener("resize", resizeHandler);

        return () => {
            window.removeEventListener("resize", resizeHandler);
        };
        // Mount-only: copy-quote setup and resize listener.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateSelectedClientLead = (value) => {
        // zohoUser.id = value.zohoId
        // zohoUser.type = value.zohoModule
        setZohoUser({
            id: value.zohoId,
            type: value.zohoModule,
        });
    };

    const updateInputHandler = (index, key, value) => {
        setJourneyData((prevJourneys) => {
            const next = [...prevJourneys];
            const originalJourney = next[index] || {};
            next[index] = { ...originalJourney, [key]: value };

            if (tripType === "returns") {
                return syncReturnLegs(next, index, key);
            }

            return next;
        });
    };

    const getCopiedProperty = (property, leg) => {
        if (quoteOrBookingToCopy) {
            let dataSource = quoteOrBookingToCopy;
            let newLeg = true;

            if (typeof leg === "number" && dataSource.legs) {
                if (dataSource.legs.length > leg) {
                    dataSource = dataSource.legs[leg];
                    newLeg = false;
                } else if (dataSource.legs.length === 0 && leg === 0) {
                    newLeg = false;
                }
            }

            if (!newLeg) {
                if (property === "flightFrom") {
                    return {
                        name: dataSource.flightFromLong,
                        city: "",
                        icao: dataSource.flightFromIcao,
                    };
                }
                if (property === "flightTo") {
                    return {
                        name: dataSource.flightToLong,
                        city: "",
                        icao: dataSource.flightToIcao,
                    };
                }
                if (property === "departureDate") {
                    const date = dataSource.departureDate.split("/");

                    return new Date(date[2], date[0] - 1, date[1]);
                }
                if (property === "departureTime") {
                    let time = dataSource.departureTime.split(":");
                    let date = new Date();
                    date.setHours(time[0], time[1], 0);
                    return date;
                }
                if (property === "passengers") {
                    const value = quoteOrBookingToCopy.passengers;

                    return {
                        label: value,
                        value: value,
                    };
                }
                if (property === "luggage") {
                    const value = quoteOrBookingToCopy.luggage;

                    return {
                        label: value,
                        value: value,
                    };
                }
            }
        }
    };

    const getCopiedDate = (i) => {
        let copiedDate = getCopiedProperty("departureDate", i);

        if (!copiedDate && journeyData[i]["date"]) {
            const date = journeyData[i]["date"].split("/");

            copiedDate = new Date(date[2], date[1] - 1, date[0]);
        }

        return copiedDate;
    };

    const getModalCloseButtonColor = () => {
        if (poweredByCtx.isPoweredBy && poweredByCtx.tenant.isDarkQuoteModalHeader) {
            return "black";
        } else {
            return "white";
        }
    };

    const toggleForMobile = () => {
        setMobileIsActive(!mobileIsActive);
    };

    const getAddIcon = () => {
        let name = "add-leg";

        if (poweredByCtx.isPoweredBy) {
            if (onPage) {
                name += "-alt";
            }
            if (isScrolled) {
                name += "-scrolled";
            }
        }

        return name;
    };

    const getDeleteIcon = () => {
        let name = "remove-leg";

        if (poweredByCtx.isPoweredBy) {
            if (onPage) {
                name += "-alt";
            }
            if (isScrolled) {
                name += "-scrolled";
            }
        }

        return name;
    };

    return (
        <>
            <div ref={quoteQueryMarkerRef2} className="quote-query-marker" style={{ position: "relative", top: -56 }} />
            <div ref={quoteQueryMarkerRef} className="quote-query-marker" style={{ height: quoteQueryMarkerHeight }} />
            <div
                ref={quoteQueryRef}
                className={getClassName([
                    "quote-query",
                    "quote-query--portal",
                    inModal ? "quote-query--in-modal" : "",
                    onPage ? "quote-query--on-page" : "",
                    isScrolled ? "quote-query--scrolled" : "",
                    isScrolled && tripType === "oneway" ? "quote-query--scrolled-oneway" : "",
                    isScrolled && tripType === "returns" ? "quote-query--scrolled-returns" : "",
                    isScrolled && tripType === "multilegs" ? "quote-query--scrolled-multilegs" : "",
                    !isScrolled && tripType === "oneway" ? "quote-query--oneway" : "",
                    !isScrolled && tripType === "returns" ? "quote-query--returns" : "",
                    !isScrolled && tripType === "multilegs" ? "quote-query--multilegs" : "",
                    checkOverflow() ? "quote-query--scrolled-overflow" : "",
                    skeleton ? "quote-query--skeleton" : "",
                ])}
            >
                {inModal && returnMessage.success ? (
                    <>
                        <PortalQuoteQueryPersonalDetails
                            journeyData={journeyData}
                            resetJourney={resetJourneyHandler}
                            journeyIsValid={formIsValid}
                            closeModal={closeQuoteModal}
                            inModal={inModal}
                        />
                    </>
                ) : (
                    <div
                        className={getClassName([
                            "quote-query__content",
                            isScrolled ? "wrap" : "",
                            mobileIsActive ? "quote-query__content--active" : "",
                        ])}
                    >
                        {mobileIsActive && <div className="quote-query__sticky-title">Private Jets</div>}

                        {/* Trip Type Tabs for sticky view */}
                        {isScrolled && (
                            <TripTypeTabs
                                tripType={tripType}
                                onTripTypeChange={handleTripTypeChange}
                                className="quote-query__trip-type-tabs"
                            />
                        )}

                        <form onSubmit={submitHandler}>
                            {/* Trip Type Tabs for main form view - only show when onPage is true (other pages, not homepage) */}
                            {!isScrolled && onPage && (
                                <TripTypeTabs
                                    tripType={tripType}
                                    onTripTypeChange={handleTripTypeChange}
                                    className="quote-query__trip-type-tabs"
                                />
                            )}

                            <>
                                <div className="quote-query__inputs">
                                    <div className="quote-query__inputs__column quote-query__inputs__column--full-width">
                                        <LeadClientInput
                                            value={(fromValue) => updateSelectedClientLead(fromValue)}
                                            required={false}
                                            label="Lead/Client"
                                            validateForm={validateForm}
                                            onFocus={onFocus}
                                            skeleton={false}
                                        />
                                    </div>
                                    <div className="quote-query__inputs__column quote-query__inputs__column--full-width portal-quote-query__inputs__column--tenant">
                                        <PortalTenantIdInput
                                            value={(fromValue) => setSelectedTenantId(fromValue.key)}
                                        />
                                    </div>
                                </div>
                            </>

                            {journeyData.map((journey, i) => {
                                return (
                                    <div
                                        key={journey.id}
                                        className={getClassName([
                                            "quote-query__inputs",
                                            canDeleteJourneyLeg(journeyData.length, tripType) &&
                                                "quote-query__inputs--with-delete",
                                        ])}
                                    >
                                        <div className="quote-query__inputs__column">
                                            <FromToInput
                                                value={(fromValue) => updateInputHandler(i, "flightFrom", fromValue)}
                                                defaultValue={journeyData[i]["flightFrom"]}
                                                direction="from"
                                                label="From"
                                                required={true}
                                                errorOnBlur={false}
                                                altErrorStyle={true}
                                                validateForm={validateForm}
                                                onFocus={onFocus}
                                                copiedProperty={getCopiedProperty("flightFrom", i)}
                                                key={`from-${resetFormRef}`}
                                                skeleton={skeleton}
                                            />
                                        </div>
                                        <div className="quote-query__inputs__column">
                                            <FromToInput
                                                value={(toValue) => updateInputHandler(i, "flightTo", toValue)}
                                                defaultValue={journeyData[i]["flightTo"]}
                                                direction="to"
                                                label="To"
                                                required={true}
                                                errorOnBlur={false}
                                                validateForm={validateForm}
                                                onFocus={onFocus}
                                                copiedProperty={getCopiedProperty("flightTo", i)}
                                                key={`to-${resetFormRef}`}
                                                skeleton={skeleton}
                                            />
                                        </div>
                                        <div
                                            className={getClassName([
                                                "quote-query__inputs__column",
                                                "quote-query__inputs__column--medium-width",
                                                "portal-quote-query__inputs__column--time-and-date",
                                                (i === 0 || tripType === "returns") &&
                                                    "quote-query__inputs__column--time-and-date",
                                            ])}
                                        >
                                            <DatePickerInput
                                                value={(dateValue) => updateInputHandler(i, "date", dateValue)}
                                                defaultValue={journeyData[i]["date"]}
                                                label="Date"
                                                required={true}
                                                errorOnBlur={false}
                                                validateForm={validateForm}
                                                onFocus={onFocus}
                                                copiedProperty={getCopiedDate(i)}
                                                inModal={inModal}
                                                key={`date-${resetFormRef}`}
                                                skeleton={skeleton}
                                                minDate={setMinDate(i, journeyData)}
                                                maxDate={setMaxDate(i, journeyData)}
                                            />
                                        </div>
                                        <div className="quote-query__inputs__column quote-query__inputs__column--medium-width portal-quote-query__inputs__column--time">
                                            <TimePickerInput
                                                is24Hour={location?.country !== "US"}
                                                value={(timeValue) => updateInputHandler(i, "time", timeValue)}
                                                defaultValue={journeyData[i]["time"]}
                                                label="Time"
                                                required={true}
                                                errorOnBlur={false}
                                                validateForm={validateForm}
                                                onFocus={onFocus}
                                                copiedProperty={getCopiedProperty("departureTime", i)}
                                                minTime={setMinTime(i, journeyData, location?.country !== "US")}
                                                key={`time-${resetFormRef}`}
                                                skeleton={skeleton}
                                            />
                                        </div>
                                        <div className="quote-query__inputs__column quote-query__inputs__column--shared">
                                            <PassengerDropdown
                                                icon={personIcon}
                                                id={`passengers-${i}`}
                                                defaultValue={journeyData[i]["passengers"]}
                                                value={(passengersValue) =>
                                                    updateInputHandler(i, "passengers", passengersValue)
                                                }
                                                classNames={[
                                                    "passenger-dropdown--flight-enquiry",
                                                    "passenger-dropdown--full",
                                                ]}
                                                validateForm={validateForm}
                                                onFocus={onFocus}
                                                key={`passengers-${resetFormRef}`}
                                                skeleton={skeleton}
                                                min={1}
                                                max={99}
                                                isOpen={openDropdownId === `passengers-${i}`}
                                                onToggle={(id, isOpen) => {
                                                    setOpenDropdownId(isOpen ? id : null);
                                                }}
                                            />
                                            <LuggageDropdown
                                                icon={luggageIcon}
                                                id={`luggage-${i}`}
                                                defaultValue={journeyData[i]["luggage"]}
                                                defaultCarryOn={journeyData[i]["carryOn"]}
                                                value={({ bags, carryOn }) => {
                                                    updateInputHandler(i, "luggage", bags);
                                                    updateInputHandler(i, "carryOn", carryOn);
                                                }}
                                                classNames={[
                                                    "luggage-dropdown--flight-enquiry",
                                                    "luggage-dropdown--full",
                                                ]}
                                                validateForm={validateForm}
                                                onFocus={onFocus}
                                                key={`luggage-${resetFormRef}`}
                                                skeleton={skeleton}
                                                min={0}
                                                max={99}
                                                isOpen={openDropdownId === `luggage-${i}`}
                                                onToggle={(id, isOpen) => {
                                                    setOpenDropdownId(isOpen ? id : null);
                                                }}
                                            />
                                        </div>
                                        {inModal && (
                                            <TextAreaInput
                                                id="requests"
                                                maxLength={2000}
                                                showCharacterCount
                                                label="Requests e.g. pets… (optional)"
                                                key={`requests-${resetFormRef}`}
                                            />
                                        )}

                                        {canDeleteJourneyLeg(journeyData.length, tripType) && (
                                            <button
                                                type="button"
                                                onClick={() => removeJourneyHandler(i)}
                                                className="quote-query__delete-button"
                                            >
                                                {inModal ? (
                                                    <img alt="" src={closeCrossDark} />
                                                ) : (
                                                    <>
                                                        {poweredByCtx.isPoweredBy ? (
                                                            <>{getRawIcon("remove-leg")}</>
                                                        ) : (
                                                            <img alt="" src={getIcon(getDeleteIcon())} />
                                                        )}
                                                    </>
                                                )}
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                );
                            })}

                            {tripType === "multilegs" && (
                                <div className="quote-query__add-leg-wrapper">
                                    <button
                                        type="button"
                                        className="quote-query__actions__add-leg-button"
                                        onClick={addJourneyHandler}
                                    >
                                        {inModal ? (
                                            <img alt="" src={plusDarkIcon} />
                                        ) : (
                                            <>
                                                {poweredByCtx.isPoweredBy ? (
                                                    <>{getRawIcon("add-leg")}</>
                                                ) : (
                                                    <img alt="" src={getIcon(getAddIcon())} />
                                                )}
                                            </>
                                        )}
                                        Add leg
                                    </button>
                                </div>
                            )}

                            <>
                                <div className="quote-query__inputs">
                                    <div className="quote-query__inputs__column quote-query__inputs__column--full-width quote-query__inputs__column--currency">
                                        <SelectInput
                                            ariaLabel="Preferred currency"
                                            icon={currencyIcon}
                                            options={currencyList}
                                            value={(value) => setSelectedPrefCurrency(value)}
                                            defaultValue={selectedPrefCurrency || "gbp"}
                                            chevron={chevronDown}
                                            classNames={["select-input--flight-enquiry", "select-input--full"]}
                                            validateForm={validateForm}
                                            onFocus={onFocus}
                                            skeleton={skeleton}
                                        />
                                    </div>
                                    <div className="quote-query__inputs__column quote-query__inputs__column--full-width quote-query__inputs__column--notes">
                                        <TextInput
                                            id="flight-notes"
                                            maxLength={2000}
                                            showCharacterCount
                                            value={(fromValue) => setCurrentNotes(fromValue)}
                                            label="Notes for flight"
                                            classNames={["text-input--flight-enquiry"]}
                                        />
                                    </div>
                                </div>
                            </>
                        </form>

                        <div className="quote-query__actions">
                            {showError && (
                                <div className="quote-query__error">
                                    <img src={exclamationIcon} alt="" />
                                    Please complete your journey details.
                                </div>
                            )}

                            <div className="quote-query__submit-container">
                                <Button
                                    disabled={sending}
                                    onClick={submitHandler}
                                    classNames={["button--full", "button--white-border"]}
                                >
                                    Submit request
                                </Button>

                                <Button
                                    disabled={sending}
                                    onClick={submitToRFQHandler}
                                    classNames={["button--full", "button--white-border"]}
                                >
                                    Ready for quote
                                </Button>

                                {portalCtx.isAdmin() && (
                                    <Button
                                        disabled={sending}
                                        onClick={submitToAvinodeHandler}
                                        classNames={["button--full", "button--white-border"]}
                                    >
                                        Submit Avinode
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {isScrolled && (
                    <Button classNames={["quote-query__expand-button"]} onClick={toggleForMobile}>
                        {poweredByCtx.isPoweredBy ? (
                            <>{getRawIcon("chevron-down-sticky-quote")}</>
                        ) : (
                            <img src={getIcon("chevron-down-sticky-quote")} alt="" />
                        )}
                        {mobileIsActive ? "Close quote" : "Start a quote"}
                    </Button>
                )}
            </div>

            {avinodeUrl && (
                <Modal
                    classNames={["modal__content--thin"]}
                    modalVisible={avinodeUrl}
                    closeModal={() => closeAvinodeModal()}
                >
                    <PortalAvinodeCtaModal closeModal={() => closeAvinodeModal()} url={avinodeUrl} />
                </Modal>
            )}

            {!inModal && (
                <Modal
                    modalVisible={modalVisible}
                    closeCrossColour={getModalCloseButtonColor()}
                    closeModal={closeModal}
                    classNames={["modal__content--quote-personal-details"]}
                >
                    <PortalQuoteQueryPersonalDetails
                        is24Hour={location?.country !== "US"}
                        journeyData={journeyData}
                        resetJourney={resetJourneyHandler}
                        journeyIsValid={formIsValid}
                        closeModal={closeModal}
                    />
                </Modal>
            )}

            {sending && <LoadingOverlay />}
        </>
    );
};

export default PortalQuoteQueryJets;
